/**
 * Whether this browser can install the app, how, and whether now is the moment
 * to say so (SPEC-016).
 *
 * Two questions that look like one. CAN it be installed is a fact about the
 * browser, and the answer is never a plain yes or no: Chromium hands the page a
 * `beforeinstallprompt` event it can fire itself, iOS Safari has no such event
 * and installs only through the share sheet, and desktop Safari and Firefox each
 * hide the action somewhere else again. A card whose button says "Install" is a
 * lie on three of those five, which is why the support state carries the
 * instruction with it.
 *
 * SHOULD we say so is a judgement, and it is the one that keeps the offer
 * non-disturbing: an app that asks to be installed before it has been used is
 * asking a stranger to move in. Pure — the browser facts arrive as arguments, so
 * every branch is reachable from a plain Node process.
 */

/** How this browser installs, most actionable first. */
export const INSTALL_SUPPORT = {
    installed: 'installed',
    nativePrompt: 'native-prompt',
    iosShare: 'ios-share',
    safariDock: 'safari-dock',
    firefoxAndroid: 'firefox-android',
    chromiumMenu: 'chromium-menu',
    unsupported: 'unsupported',
};

/**
 * The instruction to print under the offer, per support state.
 *
 * Only `native-prompt` has a button that installs anything; every other state
 * needs the sentence, because the primary action can do no more than repeat it.
 */
const HOW_TO_KEYS = {
    [INSTALL_SUPPORT.iosShare]: 'pwa.install.how.iosShare',
    [INSTALL_SUPPORT.safariDock]: 'pwa.install.how.safariDock',
    [INSTALL_SUPPORT.firefoxAndroid]: 'pwa.install.how.firefoxAndroid',
    [INSTALL_SUPPORT.chromiumMenu]: 'pwa.install.how.chromiumMenu',
};

/** A dismissed offer is not a refused one, but it is not a question to re-ask soon. */
export const INSTALL_DISMISSAL_HOLD_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Not on the first visit.
 *
 * The install offer is a claim that the app is worth keeping, and on visit one
 * the visitor has no way to judge that. Two is the smallest number that means
 * "came back".
 */
export const VISITS_BEFORE_INSTALL_OFFER = 2;

export function readInstallSupport({
    userAgent = '',
    platform = '',
    maxTouchPoints = 0,
    isStandalone = false,
    hasDeferredPrompt = false,
} = {}) {
    if (isStandalone) {
        return INSTALL_SUPPORT.installed;
    }

    // iPadOS reports itself as a Mac and is separated from a real one only by
    // the touch points — without this an iPad is told to drag the app to a dock
    // it does not have.
    const isIOS = /iPad|iPhone|iPod/u.test(userAgent) || (platform === 'MacIntel' && Number(maxTouchPoints) > 1);
    const isAndroid = /Android/iu.test(userAgent);
    // Chrome, Firefox and Edge on iOS are Safari's engine wearing another
    // badge, and they announce themselves as CriOS, FxiOS and EdgiOS. This flag
    // is about the ENGINE, and is used below only to tell a real desktop Safari
    // from a Chromium on a Mac.
    const isSafariWebKit = /WebKit/iu.test(userAgent) && !/CriOS|FxiOS|EdgiOS|OPiOS/iu.test(userAgent);
    const isChromiumFamily = /Chrome|Chromium|Edg|OPR|SamsungBrowser/iu.test(userAgent);
    const isFirefoxFamily = /Firefox|FxiOS/iu.test(userAgent);
    const isMac = /Mac/iu.test(platform) && !isIOS;
    const isWindows = /Win/iu.test(platform);

    // The event outranks every guess below it: a browser that handed us a
    // prompt can install, whatever its user agent claims to be.
    if (hasDeferredPrompt) {
        return INSTALL_SUPPORT.nativePrompt;
    }

    // EVERY browser on iOS, not only Safari.
    //
    // This is the one place this module knowingly departs from the habb.me
    // implementation it was ported from. There, the iOS branch required the
    // Safari engine as well as the platform, so Chrome, Firefox and Edge on
    // iPhone matched no branch at all and fell through to `unsupported` — no
    // card, no instruction, on browsers that install perfectly well. iOS has
    // exactly one door regardless of badge, and since 16.4 every browser on it
    // can put an app on the home screen through the share sheet.
    if (isIOS) {
        return INSTALL_SUPPORT.iosShare;
    }

    // `!isChromiumFamily` is load-bearing and was missing once: EVERY desktop
    // Chromium user agent contains "AppleWebKit", so without it Chrome and Edge
    // on a Mac match the Safari engine test and get sent to Safari's File menu
    // for an Add to Dock item they do not have.
    if (isMac && isSafariWebKit && !isChromiumFamily) {
        return INSTALL_SUPPORT.safariDock;
    }

    // Firefox for Android installs from its menu. Desktop Firefox has had no
    // install path at all since site-specific browsers were removed in 85, so
    // the honest answer there is to say nothing rather than to name a menu item
    // that is not in the menu.
    if (isFirefoxFamily) {
        return isAndroid ? INSTALL_SUPPORT.firefoxAndroid : INSTALL_SUPPORT.unsupported;
    }

    if (isChromiumFamily || isAndroid || isMac || isWindows) {
        return INSTALL_SUPPORT.chromiumMenu;
    }

    return INSTALL_SUPPORT.unsupported;
}

/** An installed app has nothing to offer; an unknown browser has nothing to say. */
export function canOfferInstall(support) {
    return support !== INSTALL_SUPPORT.installed && support !== INSTALL_SUPPORT.unsupported;
}

export function installHowToKey(support) {
    return HOW_TO_KEYS[support] || null;
}

/**
 * The label for a button that installs — and `null` where there is no such
 * button to draw.
 *
 * `native-prompt` is the only state the page can act on. Everywhere else the
 * instruction under the heading IS the action, and a button beside it could
 * only repeat the sentence while looking like it did something. A control that
 * does nothing is worse than no control, so there is no control.
 */
export function installActionKey(support) {
    return support === INSTALL_SUPPORT.nativePrompt ? 'pwa.install.action.install' : null;
}

/**
 * Is now the moment?
 *
 * Returns the reason it is not, rather than a bare false, so the hook can be
 * debugged from its return value instead of from a breakpoint.
 */
export function resolveInstallOffer({
    support = INSTALL_SUPPORT.unsupported,
    visitCount = 0,
    dismissedAt = null,
    installedAt = null,
    now = 0,
    holdMs = INSTALL_DISMISSAL_HOLD_MS,
} = {}) {
    if (!canOfferInstall(support)) {
        return { canOffer: false, reason: support === INSTALL_SUPPORT.installed ? 'installed' : 'unsupported' };
    }

    // Standalone is the only signal that says "installed", and the tab that DID
    // the installing never has it — so without this the card returns to the one
    // person who provably does not need it, carrying the browser-menu
    // instruction, on the next navigation back to a tabbed screen.
    if (Number.isFinite(installedAt) && installedAt > 0) {
        return { canOffer: false, reason: 'installed' };
    }

    if (visitCount < VISITS_BEFORE_INSTALL_OFFER) {
        return { canOffer: false, reason: 'too-early' };
    }

    if (Number.isFinite(dismissedAt) && dismissedAt > 0 && now - dismissedAt < holdMs) {
        return { canOffer: false, reason: 'dismissed' };
    }

    return { canOffer: true, reason: null };
}
